import React, { useState, useEffect } from 'react';
import { SummaryRenderer } from '../patient/SummaryRenderer';

function PatientDetail({ patient, user, onBack }) {
  const [activeTab, setActiveTab] = useState(null);
  const [reports, setReports] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportSummaries, setReportSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState({});
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectedSummary, setSelectedSummary] = useState('');
  const [selectedSummaryLoading, setSelectedSummaryLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiPeriod, setAiPeriod] = useState('all');
  const [aiLoading, setAiLoading] = useState(false);

  // Feedback states
  const [openFeedback, setOpenFeedback] = useState(null); // reportId of open feedback panel
  const [feedbackInput, setFeedbackInput] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState({}); // { reportId: [{id, feedback, createdAt, doctorName}] }
  const [holisticFeedback, setHolisticFeedback] = useState('');
  const [holisticLoading, setHolisticLoading] = useState(false);
  const [holisticSaved, setHolisticSaved] = useState(false);
  const [vitals, setVitals] = useState([]);
  const [vitalsLoading, setVitalsLoading] = useState(false);

  useEffect(() => { fetchAll(); fetchVitals(); }, []); // eslint-disable-line

  const fetchVitals = async () => {
    setVitalsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Vitals/${patient.patientId}`);
      if (res.ok) setVitals(await res.json());
    } catch { }
    setVitalsLoading(false);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rRes, cRes, pRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/Reports/${patient.patientId}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/Conditions/${patient.patientId}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/Medication/prescriptions/${patient.patientId}`),
      ]);
      if (rRes.ok) setReports(await rRes.json());
      if (cRes.ok) setConditions(await cRes.json());
      if (pRes.ok) setPrescriptions(await pRes.json());
    } catch { }
    setLoading(false);
  };

  const fetchFeedbackHistory = async (reportId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/feedback/${reportId}`);
      if (res.ok) {
        const data = await res.json();
        setFeedbackHistory(prev => ({ ...prev, [reportId]: data }));
      }
    } catch { }
  };

  const handleOpenFeedback = (reportId) => {
    if (openFeedback === reportId) { setOpenFeedback(null); return; }
    setOpenFeedback(reportId);
    setFeedbackInput('');
    fetchFeedbackHistory(reportId);
  };

  const handleSendFeedback = async (reportId) => {
    if (!feedbackInput.trim()) return;
    setFeedbackLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId, doctorId: user.id, feedback: feedbackInput.trim() })
      });
      if (res.ok) {
        setFeedbackInput('');
        fetchFeedbackHistory(reportId);
      }
    } catch { }
    setFeedbackLoading(false);
  };

  const handleDeleteFeedback = async (feedbackId, reportId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/feedback/${feedbackId}`, { method: 'DELETE' });
      if (res.ok) fetchFeedbackHistory(reportId);
    } catch { }
  };

  const handleReportSummary = async (reportId) => {
    setSummaryLoading(prev => ({ ...prev, [reportId]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/summary-report/${reportId}`);
      const data = await res.json();
      setReportSummaries(prev => ({ ...prev, [reportId]: data.summary }));
    } catch { }
    setSummaryLoading(prev => ({ ...prev, [reportId]: false }));
  };

  const handleSelectedSummary = async () => {
    if (selectedReports.length === 0) return;
    setSelectedSummaryLoading(true); setSelectedSummary('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/selected-summary`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportIds: selectedReports, doctorId: user.id })
      });
      const data = await res.json();
      setSelectedSummary(data.summary || '');
    } catch { }
    setSelectedSummaryLoading(false);
  };

  const handleAiSummary = async () => {
    setAiLoading(true); setAiSummary('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/summary/${patient.patientId}?period=${aiPeriod}`);
      const data = await res.json();
      setAiSummary(data.summary || data.overallSummary || '');
    } catch { }
    setAiLoading(false);
  };

  const handleSaveHolisticFeedback = async () => {
    if (!holisticFeedback.trim()) return;
    setHolisticLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/holistic-feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: patient.patientId, doctorId: user.id, feedback: holisticFeedback })
      });
      if (res.ok) { setHolisticSaved(true); setHolisticFeedback(''); setTimeout(() => setHolisticSaved(false), 3000); }
    } catch { }
    setHolisticLoading(false);
  };

  const toggleReport = (id) => {
    setSelectedReports(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    setSelectedSummary('');
  };

  const tabs = [
    { id: 'reports', icon: 'description', label: 'Reports' },
    { id: 'vitals', icon: 'monitor_heart', label: 'Vitals' },
    { id: 'conditions', icon: 'health_and_safety', label: 'Conditions' },
    { id: 'prescriptions', icon: 'medication', label: 'Prescriptions' },
    { id: 'feedback', icon: 'rate_review', label: 'Feedback' },
    { id: 'ai', icon: 'auto_awesome', label: 'Summary' },
  ];

  return (
    <div className="w-full h-full flex flex-col px-4 py-3 space-y-3 box-border bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      <div className="flex items-center gap-3 shrink-0 w-full">
        <button onClick={() => activeTab ? setActiveTab(null) : onBack()}
          className="w-9 h-9 rounded-full bg-white shadow-xs flex items-center justify-center active:scale-95 shrink-0">
          <span className="material-symbols-outlined text-xl text-[#1D4ED8]">arrow_back</span>
        </button>
        <h1 className="text-xl font-black text-[#0F172A] tracking-tight truncate">
          {activeTab ? tabs.find(t => t.id === activeTab)?.label : patient.patientName || 'Patient Details'}
        </h1>
      </div>

      <div className="w-full rounded-3xl p-4 text-white shadow-lg bg-gradient-to-r from-[#1D4ED8] via-[#6366F1] to-[#8B5CF6] shrink-0">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 bg-white/20 border-2 border-white/40 rounded-full flex items-center justify-center text-xl font-black text-white shrink-0">
            {(patient.patientName || 'P').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-extrabold text-base leading-tight truncate">{patient.patientName || `Patient #${patient.patientId}`}</h2>
            <p className="text-white/80 text-[11px] font-mono mt-0.5 truncate">{patient.patientUid}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="bg-emerald-400/20 text-emerald-100 border border-emerald-300/30 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>Verified
              </span>
              <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <span className="material-symbols-outlined text-xs">folder</span>{reports.length} Reports
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu List — when no tab selected */}
      {!activeTab && (
        <div className="flex-1 w-full overflow-y-auto space-y-2 min-h-0">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="w-full bg-white rounded-2xl px-4 py-3.5 flex items-center gap-4 border border-slate-200/70 shadow-xs active:scale-[0.99] transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#1D4ED8] text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>{tab.icon}</span>
              </div>
              <span className="flex-1 text-left font-bold text-slate-800 text-sm">{tab.label}</span>
              <span className="material-symbols-outlined text-slate-300 text-lg">chevron_right</span>
            </button>
          ))}
        </div>
      )}

      {/* Content — when tab selected */}
      {activeTab && (
        <div className="flex-1 w-full overflow-y-auto space-y-3 pr-0.5 min-h-0">

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <div className="w-full space-y-3">
            {selectedReports.length > 0 && (
              <div className="w-full bg-gradient-to-r from-[#1D4ED8] to-[#8B5CF6] text-white rounded-2xl p-3 flex items-center justify-between shadow-xs">
                <span className="text-xs font-bold">{selectedReports.length} selected</span>
                <div className="flex gap-2">
                  <button onClick={() => { setSelectedReports([]); setSelectedSummary(''); }} className="px-2.5 py-1 bg-white/20 text-white text-xs font-bold rounded-lg active:scale-95">Clear</button>
                  <button onClick={handleSelectedSummary} disabled={selectedSummaryLoading} className="px-2.5 py-1 bg-white text-[#1D4ED8] text-xs font-extrabold rounded-lg active:scale-95 disabled:opacity-50">
                    {selectedSummaryLoading ? '...' : '✨ Summarize'}
                  </button>
                </div>
              </div>
            )}

            {selectedSummary && (
              <div className="w-full bg-purple-50/80 rounded-2xl p-3.5 border border-purple-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-extrabold text-purple-900 text-xs flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-purple-600" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
                    Selected Reports Summary
                  </p>
                  <button onClick={() => setSelectedSummary('')} className="w-5 h-5 bg-purple-200 rounded-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-purple-800 text-xs">close</span>
                  </button>
                </div>
                <SummaryRenderer text={selectedSummary} />
              </div>
            )}

            {loading && <p className="text-center text-slate-400 text-xs py-6">Loading reports...</p>}
            {!loading && reports.length === 0 && (
              <div className="w-full bg-white rounded-3xl p-8 text-center border border-slate-200/60 shadow-xs">
                <span className="material-symbols-outlined text-slate-300 text-4xl">description</span>
                <p className="text-slate-400 text-xs font-semibold mt-2">No patient reports found</p>
              </div>
            )}

            {reports.map((report) => {
              const isSelected = selectedReports.includes(report.id);
              const isFeedbackOpen = openFeedback === report.id;
              const history = feedbackHistory[report.id] || [];
              return (
                <div key={report.id} className={`w-full bg-white rounded-3xl border p-3.5 shadow-xs space-y-3 transition-all ${isSelected ? 'border-[#1D4ED8] ring-1 ring-[#1D4ED8]' : 'border-slate-200/70'}`}>
                  <div className="flex items-start gap-3 w-full">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleReport(report.id)}
                      className="w-4 h-4 mt-1 rounded border-slate-300 text-[#1D4ED8] focus:ring-0 cursor-pointer shrink-0" />
                    <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center shrink-0 border border-red-100">
                      <span className="material-symbols-outlined text-red-500 text-lg">picture_as_pdf</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-extrabold text-xs text-slate-900 truncate">{report.fileName}</h4>
                      <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                        {new Date(report.uploadDate || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 mt-1 ${report.extractedText ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
                        <span className="material-symbols-outlined text-[10px]" style={{ fontVariationSettings: '"FILL" 1' }}>{report.extractedText ? 'check_circle' : 'hourglass_top'}</span>
                        {report.extractedText ? 'Processed' : 'Processing'}
                      </span>
                    </div>
                  </div>

                  {/* 4 Action Buttons */}
                  <div className="grid grid-cols-4 gap-1.5 w-full">
                    <button onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/Reports/view/${report.id}`, '_blank')}
                      className="py-2 bg-slate-50 border border-slate-200 text-slate-700 font-bold rounded-xl text-[10px] flex items-center justify-center gap-1 active:scale-95">
                      <span className="material-symbols-outlined text-xs">visibility</span>View
                    </button>
                    <button onClick={async () => {
                      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/download/${report.id}`);
                      const blob = await res.blob();
                      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = report.fileName; a.click();
                    }} className="py-2 bg-[#0B3C95] text-white font-bold rounded-xl text-[10px] flex items-center justify-center gap-1 active:scale-95">
                      <span className="material-symbols-outlined text-xs">download</span>Save
                    </button>
                    <button onClick={() => handleReportSummary(report.id)} disabled={summaryLoading[report.id]}
                      className="py-2 bg-purple-50 text-purple-700 border border-purple-100 font-bold rounded-xl text-[10px] flex items-center justify-center gap-1 active:scale-95 disabled:opacity-50">
                      <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
                      {summaryLoading[report.id] ? '...' : 'AI'}
                    </button>
                    <button onClick={() => handleOpenFeedback(report.id)}
                      className={`py-2 font-bold rounded-xl text-[10px] flex items-center justify-center gap-1 active:scale-95 border ${isFeedbackOpen ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                      <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: '"FILL" 1' }}>rate_review</span>
                      Feedback
                    </button>
                  </div>

                  {/* Feedback Panel */}
                  {isFeedbackOpen && (
                    <div className="w-full bg-blue-50 rounded-2xl p-3 border border-blue-100 space-y-3">
                      <p className="text-xs font-black text-blue-700 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: '"FILL" 1' }}>rate_review</span>
                        Doctor's Feedback
                      </p>

                      {/* Feedback History */}
                      {history.length > 0 && (
                        <div className="space-y-2">
                          {history.map((fb) => (
                            <div key={fb.id} className="bg-white rounded-xl p-2.5 border border-blue-100 flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-blue-500 font-bold mb-0.5">{new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                <p className="text-xs text-slate-700 leading-relaxed">{fb.feedback}</p>
                              </div>
                              <button onClick={() => handleDeleteFeedback(fb.id, report.id)}
                                className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center shrink-0 active:scale-95">
                                <span className="material-symbols-outlined text-red-400 text-xs">delete</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {history.length === 0 && (
                        <p className="text-xs text-slate-400 text-center py-2">No feedback sent yet</p>
                      )}

                      {/* Send Input */}
                      <div className="flex gap-2">
                        <textarea
                          placeholder="Type feedback, advice or prescription notes..."
                          value={feedbackInput}
                          onChange={e => setFeedbackInput(e.target.value)}
                          rows={2}
                          className="flex-1 text-xs bg-white border border-slate-200 rounded-xl px-3 py-2 outline-none resize-none text-slate-700 placeholder-slate-400 focus:border-[#1D4ED8]"
                        />
                        <button onClick={() => handleSendFeedback(report.id)} disabled={feedbackLoading || !feedbackInput.trim()}
                          className="w-10 h-10 self-end rounded-xl bg-[#1D4ED8] text-white flex items-center justify-center active:scale-95 disabled:opacity-40 shrink-0">
                          <span className="material-symbols-outlined text-sm">{feedbackLoading ? 'hourglass_top' : 'send'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI Summary */}
                  {reportSummaries[report.id] && (
                    <div className="w-full bg-purple-50/70 rounded-2xl p-3 border border-purple-100 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-purple-900 flex items-center gap-1">
                          <span className="material-symbols-outlined text-sm text-purple-600" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>AI Summary
                        </p>
                        <button onClick={() => setReportSummaries(prev => { const n = {...prev}; delete n[report.id]; return n; })}
                          className="w-4 h-4 rounded-full bg-purple-200 flex items-center justify-center">
                          <span className="material-symbols-outlined text-xs text-purple-800">close</span>
                        </button>
                      </div>
                      <SummaryRenderer text={reportSummaries[report.id]} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* VITALS TAB */}
        {activeTab === 'vitals' && (
          <div className="w-full space-y-3">
            {vitalsLoading && <p className="text-center text-slate-400 text-xs py-6">Loading vitals...</p>}
            {!vitalsLoading && vitals.length === 0 && (
              <div className="w-full bg-white rounded-3xl p-8 text-center border border-slate-200/60 shadow-xs">
                <span className="material-symbols-outlined text-slate-300 text-4xl">monitor_heart</span>
                <p className="text-slate-400 text-xs font-semibold mt-2">No vitals recorded by patient</p>
              </div>
            )}
            {vitals.length > 0 && (
              <>
                <div className="w-full bg-white rounded-2xl p-3.5 border border-slate-200/70 shadow-xs">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Latest Readings</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'weight', label: 'Weight', unit: 'kg', icon: 'monitor_weight', color: '#3b82f6', bg: '#eff6ff' },
                      { key: 'bloodPressure', label: 'BP', unit: 'mmHg', icon: 'favorite', color: '#ef4444', bg: '#fef2f2' },
                      { key: 'bloodGlucose', label: 'Glucose', unit: 'mg/dL', icon: 'water_drop', color: '#f59e0b', bg: '#fffbeb' },
                      { key: 'heartRate', label: 'Heart Rate', unit: 'bpm', icon: 'cardiology', color: '#ec4899', bg: '#fdf2f8' },
                      { key: 'temperature', label: 'Temp', unit: '°F', icon: 'thermometer', color: '#8b5cf6', bg: '#f5f3ff' },
                    ].map(field => {
                      const latest = vitals.find(v => v[field.key] !== null && v[field.key] !== undefined && v[field.key] !== '');
                      if (!latest) return null;
                      return (
                        <div key={field.key} className="rounded-xl p-2.5 border border-slate-100" style={{ background: field.bg }}>
                          <div className="flex items-center gap-1 mb-1">
                            <span className="material-symbols-outlined text-sm" style={{ color: field.color }}>{field.icon}</span>
                            <p className="text-[10px] font-bold text-slate-500">{field.label}</p>
                          </div>
                          <p className="font-black text-base" style={{ color: field.color }}>{latest[field.key]}</p>
                          <p className="text-[10px] text-slate-400">{field.unit}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">History ({vitals.length} records)</p>
                {vitals.map(v => (
                  <div key={v.id} className="w-full bg-white rounded-2xl p-3.5 border border-slate-200/70 shadow-xs">
                    <p className="text-xs font-black text-[#1D4ED8] mb-2">
                      {new Date(v.recordedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {v.weight && <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-xs text-blue-500">monitor_weight</span><div><p className="text-[10px] text-slate-400">Weight</p><p className="text-xs font-bold text-slate-800">{v.weight} kg</p></div></div>}
                      {v.bloodPressure && <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-xs text-red-500">favorite</span><div><p className="text-[10px] text-slate-400">BP</p><p className="text-xs font-bold text-slate-800">{v.bloodPressure} mmHg</p></div></div>}
                      {v.bloodGlucose && <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-xs text-amber-500">water_drop</span><div><p className="text-[10px] text-slate-400">Glucose</p><p className="text-xs font-bold text-slate-800">{v.bloodGlucose} mg/dL</p></div></div>}
                      {v.heartRate && <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-xs text-pink-500">cardiology</span><div><p className="text-[10px] text-slate-400">Heart Rate</p><p className="text-xs font-bold text-slate-800">{v.heartRate} bpm</p></div></div>}
                      {v.temperature && <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-xs text-purple-500">thermometer</span><div><p className="text-[10px] text-slate-400">Temp</p><p className="text-xs font-bold text-slate-800">{v.temperature} °F</p></div></div>}
                    </div>
                    {v.notes && <p className="text-[10px] text-slate-400 italic mt-2 pt-2 border-t border-slate-50">"{v.notes}"</p>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* CONDITIONS TAB */}
        {activeTab === 'conditions' && (
          <div className="w-full space-y-3">
            {loading && <p className="text-center text-slate-400 text-xs py-4">Loading conditions...</p>}
            {!loading && conditions.length === 0 && (
              <div className="w-full bg-white rounded-3xl p-8 text-center border border-slate-200/60 shadow-xs">
                <span className="material-symbols-outlined text-slate-300 text-4xl">health_and_safety</span>
                <p className="text-slate-400 text-xs font-semibold mt-2">No recorded medical conditions</p>
              </div>
            )}
            {conditions.filter(c => c.isActive).map(c => (
              <div key={c.id} className="w-full bg-white rounded-2xl p-3.5 border border-slate-200/70 shadow-xs space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-extrabold text-slate-900 text-xs">{c.conditionName}</h4>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${c.severity === 'severe' ? 'bg-red-50 text-red-600 border border-red-100' : c.severity === 'moderate' ? 'bg-amber-50 text-amber-600 border border-amber-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>{c.severity}</span>
                </div>
                {c.diagnosedDate && <p className="text-[11px] text-slate-400">Diagnosed: {new Date(c.diagnosedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                {c.notes && <p className="text-xs text-slate-600 italic">"{c.notes}"</p>}
              </div>
            ))}
          </div>
        )}

        {/* PRESCRIPTIONS TAB */}
        {activeTab === 'prescriptions' && (
          <div className="w-full space-y-3">
            {loading && <p className="text-center text-slate-400 text-xs py-4">Loading prescriptions...</p>}
            {!loading && prescriptions.length === 0 && (
              <div className="w-full bg-white rounded-3xl p-8 text-center border border-slate-200/60 shadow-xs">
                <span className="material-symbols-outlined text-slate-300 text-4xl">medication</span>
                <p className="text-slate-400 text-xs font-semibold mt-2">No active prescriptions</p>
              </div>
            )}
            {prescriptions.map(p => (
              <div key={p.id} className="w-full bg-white rounded-2xl p-3.5 border border-slate-200/70 shadow-xs flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
                  <div className="w-9 h-9 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>medication</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-extrabold text-slate-900 text-xs truncate">{p.name}</h4>
                    <p className="text-[11px] text-slate-400 truncate">{p.medicineCount} medicine(s) • {new Date(p.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
                <button onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/Medication/prescription/view/${p.id}`, '_blank')}
                  className="px-3 py-1.5 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl active:scale-95">View</button>
              </div>
            ))}
          </div>
        )}

        {/* HOLISTIC FEEDBACK TAB */}
        {activeTab === 'feedback' && (
          <div className="w-full space-y-3">
            <div className="w-full bg-white rounded-3xl p-4 border border-slate-200/70 shadow-xs space-y-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[#1D4ED8] text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>rate_review</span>
                <h3 className="font-extrabold text-slate-900 text-sm">Overall Patient Feedback</h3>
              </div>
              <p className="text-xs text-slate-400">Write holistic feedback, overall health advice or prescription for this patient.</p>
              {holisticSaved && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <span className="material-symbols-outlined text-emerald-600 text-sm">check_circle</span>
                  <p className="text-xs font-bold text-emerald-700">Feedback sent to patient successfully!</p>
                </div>
              )}
              <textarea placeholder="Write your overall assessment, health advice, prescription notes..."
                value={holisticFeedback} onChange={e => setHolisticFeedback(e.target.value)} rows={5}
                className="w-full text-sm bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 outline-none resize-none text-slate-700 placeholder-slate-400 focus:border-[#1D4ED8]" />
              <button onClick={handleSaveHolisticFeedback} disabled={holisticLoading || !holisticFeedback.trim()}
                className="w-full py-3 bg-gradient-to-r from-[#1D4ED8] to-[#8B5CF6] text-white font-extrabold rounded-2xl text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: '"FILL" 1' }}>send</span>
                {holisticLoading ? 'Sending...' : 'Send Feedback to Patient'}
              </button>
            </div>
          </div>
        )}

        {/* AI SUMMARY TAB */}
        {activeTab === 'ai' && (
          <div className="w-full space-y-3">
            <div className="w-full bg-white rounded-3xl p-3.5 border border-slate-200/70 shadow-xs space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Select Time Period</p>
              <div className="grid grid-cols-4 gap-1.5 w-full">
                {[{ id: 'all', label: 'All Time' }, { id: '1year', label: '1 Year' }, { id: '6months', label: '6 Months' }, { id: '3months', label: '3 Months' }].map(p => (
                  <button key={p.id} onClick={() => { setAiPeriod(p.id); setAiSummary(''); }}
                    className={`w-full py-1.5 rounded-xl text-[10px] font-extrabold text-center ${aiPeriod === p.id ? 'bg-gradient-to-r from-[#1D4ED8] to-[#8B5CF6] text-white shadow-xs' : 'bg-slate-50 text-slate-500 border border-slate-200'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="w-full bg-white rounded-3xl p-4 border border-slate-200/70 shadow-xs space-y-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[#1D4ED8] text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
                <h3 className="font-extrabold text-slate-900 text-xs">Overall Health Summary</h3>
              </div>
              {aiLoading && (
                <div className="flex flex-col items-center py-6">
                  <div className="flex gap-1 mb-2">{[0, 0.15, 0.3].map((d, i) => <div key={i} className="w-2 h-2 bg-[#1D4ED8] rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />)}</div>
                  <p className="text-xs text-slate-400">Analyzing patient records...</p>
                </div>
              )}
              {aiSummary && !aiLoading && <SummaryRenderer text={aiSummary} />}
              {!aiSummary && !aiLoading && (
                <button onClick={handleAiSummary} className="w-full py-3 bg-gradient-to-r from-[#1D4ED8] via-[#8B5CF6] to-[#D946EF] text-white font-extrabold rounded-2xl text-xs active:scale-95">
                  Generate {aiPeriod === 'all' ? 'Complete' : aiPeriod} Summary
                </button>
              )}
              {aiSummary && !aiLoading && (
                <button onClick={() => setAiSummary('')} className="w-full py-2 bg-slate-100 text-slate-500 font-bold rounded-xl text-xs active:scale-95">Regenerate</button>
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function MobileDoctorPatients({ user }) {
  const [patients, setPatients] = useState([]);
  const [pending, setPending] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(null);

  useEffect(() => { fetchPatients(); }, []); // eslint-disable-line

  const fetchPatients = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Access/doctor-requests/${user.id}`);
      const data = await res.json();
      setPatients(data.filter(r => r.status === 'approved'));
      setPending(data.filter(r => r.status === 'pending'));
    } catch { }
  };

  if (selectedPatient) return <PatientDetail patient={selectedPatient} user={user} onBack={() => setSelectedPatient(null)} />;

  const filtered = patients.filter(p =>
    (p.patientName || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.patientUid || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full h-full flex flex-col px-4 py-3 space-y-3 box-border bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-center justify-between w-full shrink-0">
        <div>
          <h1 className="text-xl font-black text-[#0F172A] tracking-tight">My Patients</h1>
          <p className="text-[10px] font-bold text-emerald-600 mt-0.5">{patients.length} ACTIVE <span className="text-slate-300 mx-1">•</span> <span className="text-slate-400">{pending.length} PENDING</span></p>
        </div>
        <div className="w-10 h-10 bg-purple-50 rounded-2xl flex items-center justify-center border border-purple-100 shrink-0">
          <span className="material-symbols-outlined text-[#8B5CF6] text-xl">groups</span>
        </div>
      </div>

      <div className="w-full bg-white rounded-2xl px-3 py-2.5 flex items-center border border-slate-200/80 shadow-xs shrink-0">
        <span className="material-symbols-outlined text-slate-400 text-lg mr-2">search</span>
        <input type="text" placeholder="Search by name or Patient ID..." value={search} onChange={e => setSearch(e.target.value)}
          className="bg-transparent border-none p-0 text-xs font-medium text-slate-800 w-full placeholder-slate-400 focus:outline-none" />
      </div>

      <div className="grid grid-cols-3 gap-2 w-full shrink-0">
        {[{ id: 'all', icon: 'groups', label: 'All' }, { id: 'active', icon: null, label: 'Active' }, { id: 'pending', icon: null, label: 'Pending' }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`w-full py-2 rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 ${filter === f.id ? 'bg-gradient-to-r from-[#1D4ED8] to-[#8B5CF6] text-white shadow-xs' : 'bg-white text-slate-500 border border-slate-200'}`}>
            {f.icon ? <span className="material-symbols-outlined text-sm">{f.icon}</span> : <span className={`w-2 h-2 rounded-full ${f.id === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>}
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 w-full overflow-y-auto space-y-3 pr-0.5 min-h-0">
        {(filter === 'all' || filter === 'active') && (
          <div className="w-full space-y-2">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider px-1">Active Patients</p>
            {filtered.length === 0 && <div className="w-full bg-white rounded-3xl p-8 text-center border border-slate-200/60 shadow-xs"><p className="text-slate-400 text-xs font-semibold">No active patients found</p></div>}
            {filtered.map(p => (
              <div key={p.id} onClick={() => setSelectedPatient(p)}
                className="w-full bg-white rounded-3xl p-3.5 flex items-center justify-between border border-slate-200/70 shadow-xs active:scale-[0.99] cursor-pointer">
                <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
                  <div className="w-11 h-11 rounded-full bg-indigo-50 text-[#0B3C95] font-black text-base flex items-center justify-center shrink-0 border border-indigo-100">
                    {(p.patientName || 'P').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-extrabold text-slate-900 text-xs truncate">{p.patientName || `Patient #${p.patientId}`}</h3>
                      <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-[8px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-emerald-500"></span>Active
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">{p.patientUid}</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-400 text-lg shrink-0">chevron_right</span>
              </div>
            ))}
          </div>
        )}
        {(filter === 'all' || filter === 'pending') && pending.length > 0 && (
          <div className="w-full space-y-2 pt-1">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider px-1">Pending Approval</p>
            {pending.map(p => (
              <div key={p.id} className="w-full bg-amber-50/50 rounded-3xl p-3.5 flex items-center justify-between border border-amber-200/60">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-800 font-black text-base flex items-center justify-center shrink-0">{(p.patientName || 'P').charAt(0).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-extrabold text-slate-900 text-xs truncate">{p.patientName || `Patient #${p.patientId}`}</h3>
                      <span className="bg-amber-100 text-amber-800 text-[8px] font-bold px-2 py-0.5 rounded-full shrink-0">⏳ Pending</span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">{p.patientUid}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileDoctorPatients;